import React from 'react';
import './../graphmodal/graphmodal.css';
import constants from '../../config/constants'
import toast from '../toast'
import E2Graph from './Graph';
import sensorService from '../../services/sensorService';
import DatePicker from '../datepicker'
import LoadingWheel from '../loadingwheel'
import Select from '../select';
import { Button, Tooltip } from 'antd';
import {
  CloseOutlined
} from '@ant-design/icons';
import Plotly from 'plotly.js';
import { clampEndDateForStart, clampStartDateForEnd, isGraphDateRangeValid } from '../../helpers/graphDateRange';


export interface GraphProps {
  closeGraphModal: () => void;
  SensorBoxName: string;
  sensorBox: any;
}

export interface GraphState {
  x_time: any;
  y_data: any;
  sensorData: any;
  startDate: Date;
  endDate: Date;
  isLoading: boolean;
  dateError: string;
  sensorOptions: any,
  selectedGrid: any,
  threshold?: any;
  sensorGroups: any;
  availableProps: any[];
  e2RecentData: any[];
}

const MAX_SENSORS_PER_TYPE = 6;

class E2GraphModal extends React.PureComponent<GraphProps, GraphState>{

  constructor(props: GraphProps) {
    super(props);
    this.state = {
      x_time: {},
      y_data: {},
      sensorData: [],
      startDate: this.getDateWeekAgo(),
      endDate: new Date(),
      isLoading: false,
      sensorOptions: [],
      dateError: '',
      selectedGrid: [{}],
      sensorGroups: {},
      availableProps: [],
      e2RecentData: []
    }
  }

  getDateWeekAgo() {
    const date = new Date()
    const pastDate = new Date().getDate() - 7;
    date.setDate(pastDate);
    return date
  }

  validateDate() {
    return isGraphDateRangeValid(this.state.startDate, this.state.endDate)
  }

  async componentDidMount() {
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    
    await this.getE2RecentProps()
    await this.loadE2Data()
  }

  componentWillUnmount() {
    // Restore body scroll when modal is closed
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
  }

  extractTypeFromProp = (item: any): string => {
    return item.pin_type || 'default';
  }

  async getE2RecentProps() {
    try {
      const props = await sensorService.getE2RecentProps();
      const recentData = await sensorService.getE2SensorData('');
      
      this.setState({ availableProps: props || [], e2RecentData: recentData || [] }, () => {
        this.setSensorOptions(recentData || []);
      });
    } catch (error) {
      toast.error(error as any);
    }
  }

  async setSensorOptions(recentData?: any[]) {
    const dataToUse = recentData || this.state.e2RecentData;
    let sensorOptions: any = []
    let sensorGroups: any = {}
    
    // Create sensor options from recent data
    dataToUse.forEach((item: any) => {
      const pinType = this.extractTypeFromProp(item);
      
      if (!sensorGroups[pinType]) {
        sensorGroups[pinType] = {
          type: pinType,
          sensors: [],
          displayName: pinType
        };
      }
      
      const sensorOption = { 
        _id: item.prop, 
        name: item.actual_name || item.prop, 
        representation: item.prop,
        pin_type: pinType,
        prop: item.prop
      };
      
      // Avoid duplicates
      if (!sensorOptions.find((s: any) => s._id === item.prop)) {
        sensorOptions.push(sensorOption);
        sensorGroups[pinType].sensors.push(sensorOption);
      }
    });
    
    this.setState({ sensorOptions, sensorGroups })
  }

  getSelectedE2Props = () => {
    return (this.state.selectedGrid?.flatMap((grid: any) => grid?.parameters || []) || [])
      .filter((id: string) => !!id)
  }

  async getDatainChunks(props?: string[]) {
    if (props && !props.length) {
      return []
    }
    let graphData = await sensorService.getE2GraphData(
      this.state.startDate,
      this.state.endDate,
      null,
      props
    );

    return graphData
  }
  async loadE2Data() {
    try {
      const isValidDates = this.validateDate()

      if (!isValidDates) {
        this.setState({ dateError: constants.message.dateError, isLoading: false })
        return
      }

      this.setState({ isLoading: true, dateError: '' })
      const data = await this.getDatainChunks()
      this.setState({ sensorData: data, isLoading: false }, () => {
        if (this.getSelectedE2Props().length) {
          this.setGraphData()
        } else {
          this.setState({ y_data: {}, x_time: {} })
        }
      })

    } catch (error) {
      this.setState({ isLoading: false })
      toast.error(error as any)
    }

  }

  setGraphData = async () => {
    try {
      let y_data: any = {}
      let x_time: any = {}
      let threshold:any = {}
      
      const propsToPlot = this.getSelectedE2Props()
      if (!propsToPlot.length) {
        this.setState({ y_data: {}, x_time: {}, threshold: {} })
        return
      }
      propsToPlot.forEach((prop: string) => {
        y_data[prop] = []
        x_time[prop] = []
      });

      this.state.sensorData.forEach((item: any) => {
        const prop = item.prop;
        if (prop && y_data[prop]) {
          // Extract timestamp-value pairs from the item
          // All keys except prop, actual_name, and pin_type are timestamps
          const timestampValuePairs: Array<{timestamp: number, value: any}> = [];
          Object.keys(item).forEach((key) => {
            if (key !== 'prop' && key !== 'actual_name' && key !== 'pin_type') {
              const timestamp = parseInt(key);
              const value = item[key];
              if (value !== null && value !== undefined && !isNaN(timestamp)) {
                timestampValuePairs.push({ timestamp, value });
              }
            }
          });
          
          // Sort by timestamp to ensure proper ordering
          timestampValuePairs.sort((a, b) => a.timestamp - b.timestamp);
          
          // Add to arrays
          timestampValuePairs.forEach(({ timestamp, value }) => {
            y_data[prop].push(value);
            x_time[prop].push(timestamp);
          });
        }
      })

      this.setState({ y_data, x_time, threshold })
    } catch (error) {
      toast.error(error as any)
    }
  }

  closeModal = () => {
    // Restore body scroll when modal is closed
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
    
    this.props.closeGraphModal();
    this.setState({ 
      y_data: {}, 
      x_time: {}
    })
  }

  getSelectedE2Sensors = () => {
    // Return sensors based on available props from recent data
    return this.state.e2RecentData.map((item: any) => {
      const pinType = this.extractTypeFromProp(item);
      return {
        pin_with_representation: item.prop,
        representation: item.prop,
        type: pinType,
        _id: item.prop,
        display_name: item.actual_name || item.prop,
        actual_name: item.actual_name,
        prop: item.prop
      };
    });
  }

  // handle click event of the Remove button
  handleRemoveClick = (index: any) => {
    const list = [...this.state.selectedGrid];
    list.splice(index, 1);
    this.setState({ selectedGrid: list }, () => {
      setTimeout(() => {
        const plotElements = document.querySelectorAll('.js-plotly-plot');
        plotElements.forEach((plotElement: any) => {
          if (plotElement && plotElement.data) {
            Plotly.Plots.resize(plotElement);
          }
        });
      }, 0);
    });
  };

  // handle click event of the Add button
  handleAddClick = () => {
    this.setState({ selectedGrid: [...this.state.selectedGrid, { parameters: [] }] }, () => {
      setTimeout(() => {
        const plotElements = document.querySelectorAll('.js-plotly-plot');
        plotElements.forEach((plotElement: any) => {
          if (plotElement && plotElement.data) {
            Plotly.Plots.resize(plotElement);
          }
        });
      }, 0);
    });
  };

  handleConditionDropDownChange = (option: any, index: any) => {
    const optionValue = option.map((item: any) => item.value)
    
    const sensorsByType: any = {};
    let hasError = false;
    optionValue.forEach((sensorId: string) => {
      if (hasError) return;
      const sensor = this.state.sensorOptions.find((s: any) => s._id === sensorId);
      if (sensor) {
        const pinType = sensor.pin_type || 'default';
        if (!sensorsByType[pinType]) {
          const currentTypeCount = Object.keys(sensorsByType).length;
          if (currentTypeCount >= 2) {
            toast.error('Maximum 2 sensor types allowed');
            hasError = true;
            return;
          }
          sensorsByType[pinType] = [];
        }
        sensorsByType[pinType].push(sensorId);
      }
    });
    
    if (hasError) {
      return;
    }
    
    const maxPerType = Math.max(...Object.values(sensorsByType).map((arr: any) => arr.length));
    if (maxPerType > MAX_SENSORS_PER_TYPE) {
      toast.error(`Maximum ${MAX_SENSORS_PER_TYPE} sensors allowed per type`);
      return;
    }
    
    const list = [...this.state.selectedGrid];
    list[index]['parameters'] = optionValue;
    this.setState({ selectedGrid: list }, async () => {
      await this.setGraphData()
    })
  };
  

  getParameterValues = (values: any) => {
    const parametersArray = this.state.sensorOptions?.filter((item: any) => (
      values?.includes(item?._id)
    ))
    const parameterValues = parametersArray?.map((item: any) => ({ value: item._id, label: item.name }))
    return parameterValues
  }
  

  onChangeStartDate = (value: any) => {
    const endDate = clampEndDateForStart(value, this.state.endDate)
    this.setState({ startDate: value, endDate, dateError: '' }, async () => {
      await this.loadE2Data()
    })
  }

  onChangeEndDate = async (value: any) => {
    const startDate = clampStartDateForEnd(this.state.startDate, value)
    this.setState({ startDate, endDate: value, dateError: '' }, async () => {
      await this.loadE2Data()
    })
  }

  render() {

    const graphClassName = ['multiple-graph w-100 pt-3 pb-3']
    if(this.state.selectedGrid.length > 1){
      graphClassName.push('col-6')
    }else{
      graphClassName.push('col-12')
    }

    return (
      <>
        <div 
          className="modal-backdrop fade show"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1040,
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }}
          onClick={() => { this.closeModal() }}
        />
        <div 
          className="modal fade bd-example-modal-sm show" 
          id="graphmodal" 
          role="dialog" 
          aria-labelledby="exampleModalLabel"
          onWheel={(e) => {
            // Prevent scroll propagation to body when scrolling inside modal
            e.stopPropagation();
          }}
          onTouchMove={(e) => {
            // Prevent touch scroll propagation to body
            e.stopPropagation();
          }}
          style={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1050,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div 
            className="modal-dialog modal-sensor-data-graph" 
            role="document"
            onClick={(e) => e.stopPropagation()}
            style={{
              height: '90vh',
              margin: '0 auto',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '90vh',
              width: '100%',
              maxWidth: 'calc(90% - 32px)'
            }}
          >
          <div 
            className="modal-content" 
            style={{ height: '100%', display: 'flex', flexDirection: 'column', maxHeight: '100vh' }}
            onWheel={(e) => {
              // Prevent scroll propagation to body
              e.stopPropagation();
            }}
            onTouchMove={(e) => {
              // Prevent touch scroll propagation to body
              e.stopPropagation();
            }}
          >
            <div className="modal-header ">
              <h5 className="modal-title" id="exampleModalLabel">E2 Sensor Data Graph</h5>
              <span  className="close" onClick={() => { this.closeModal() }}>&times;</span>
            </div>
            <div className="row mt-50 mb-25 ml-2 ht-custom" style={{marginRight:0, flexShrink: 0}}>
              <div className="col-xs-4 col-sm-4 col-md-4 col-lg-3 media-margin-tp">
                <div className="form-group mb-0" style={{ display: 'flex', alignItems: 'center' }}>
                  <label style={{ marginRight: '8px', marginBottom: 0 }}>From:</label>
                  <DatePicker
                    value={this.state.startDate}
                    maxDate={this.state.endDate}
                    onChange={(value: Date) => this.onChangeStartDate(value)}
                    placeholderText="Start Date"
                  />
                </div>
              </div>
              <div className="col-xs-4 col-sm-4 col-md-4 col-lg-3 media-margin-tp">
                <div className="form-group mb-0" style={{ display: 'flex', alignItems: 'center' }}>
                  <label style={{ marginRight: '8px', marginBottom: 0 }}>To:</label>
                  <DatePicker
                    value={this.state.endDate}
                    minDate={this.state.startDate}
                    maxDate={new Date()}
                    onChange={(value: Date) => this.onChangeEndDate(value)}
                    placeholderText="End Date"
                  />
                </div>
              </div>

              {this.state.sensorOptions.length > 0 &&
                <div className="col-xs-2 col-sm-2 col-md-3 col-lg-2 media-margin-tp">
                  <div className="form-group mb-0">
                    <button className="btn btn-block default-btn btn-custom" style={{borderRadius:4}} onClick={this.handleAddClick}>Add New Graph</button>
                  </div>
                </div>
              }
            </div>
            <div className="row justify-between items-center space-x-6" style={{ marginLeft:15,marginRight:0}}>              
              <div className="error-msg text-red-600" style={{ marginLeft:15}}>{this.state.dateError}</div>
            </div>

            <div 
              className="modal-body modal-height-graph px-3"
              onWheel={(e) => {
                // Prevent scroll propagation to body
                e.stopPropagation();
              }}
              onTouchMove={(e) => {
                // Prevent touch scroll propagation to body
                e.stopPropagation();
              }}
              style={{
                overflowY: 'auto',
                overflowX: 'hidden',
                flex: 1,
                minHeight: 0
              }}
            >
              <div className="d-flex flex-wrap w-100 ml-0">
                {(this.state.sensorOptions.length > 0 )?
                  this.state.selectedGrid?.map((grid: any, index: any) => {
                    const paramValues = this.getParameterValues(grid?.parameters)
                    return (
                      <>
                        <div className={graphClassName.join(' ')}>
                          <div className='border p-3 h-100'>


                            <div className='d-flex mb-3'>
                              {(this.state.sensorOptions?.length !== 0)  &&
                                <div className="flex-fill media-margin-tp select-min">
                                  <div className="form-group mb-0">
                                    <Select
                                      isMulti={true}
                                      name="parameters"
                                      data={this.state.sensorOptions}
                                      value={paramValues}
                                      onChange={(e: any) => this.handleConditionDropDownChange(e, index)}
                                      maxOptions={12}
                                    />
                                  </div>
                                </div>
                              }

                              <div className='flex-auto ml-4'>
                                <div className="form-group">
                                  {
                                    this.state.selectedGrid.length !== 1 &&
                                    <Button className="mr-3" type="primary" shape="circle" onClick={() => this.handleRemoveClick(index)} ghost>
                                      <CloseOutlined />
                                    </Button>
                                  }
                                </div>
                              </div>
                            </div>

                            {(grid?.parameters?.length > 0) ?
                              <E2Graph
                                key={index}
                                index={index}
                                gridsCount={this.state.selectedGrid?.length}
                                x={this.state.x_time}
                                y={this.state.y_data}
                                threshold={this.state.threshold}
                                param={paramValues}
                                sensor_values={this.getSelectedE2Sensors()?? []}
                                configDate=""
                                sensorBox={this.props.sensorBox}
                              />
                              :
                              <div className="no-data-message w-100 col-12">Select parameters to view the graph</div>
                            }
                          </div>
                        </div>
                      </>
                    );
                  })
                  : <div className="no-data-message w-100 col-12">No Data Available</div>}
              </div>
            </div>
            <div className="modal-footer modal-footer-custom-padding">
            </div>
          </div>
        </div>
        {this.state.isLoading ?
          <LoadingWheel />
          : null}
        </div>
      </>

    );
  }
}

export default E2GraphModal;